import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { BED_JOINT_GAP_MM, BRICK_SPECS, COORDINATE_QUANTUM_MM, MAX_PERPEND_GAP_MM, formatMm } from '@bw/core';
import { useEditor } from '../state/store.js';
import { useVolatile } from '../state/volatile.js';
import { plural } from '../copy.js';
import { X } from 'lucide-react';
import { WallsPanel } from './WallsPanel.js';
import { stageOfTool } from '../state/stages.js';

export interface InspectorProps {
  readonly onApplyJoint: (value: string) => void;
  readonly jointNote: { readonly text: string; readonly tone: 'note' | 'warn' | 'ok' } | null;
  readonly run: (id: string) => void;
  readonly onSelectWall: (id: string) => void;
  readonly onDeleteWall: (id: string) => void;
  readonly floating?: boolean;
  readonly onClose?: (() => void) | undefined;
}

/**
 * The walls list, then whatever the current stage has to say about the wall
 * you are on.
 *
 * Walls sit at the top in every stage — they are the one thing that is true
 * whichever step you are on — while the elevation blocks below them belong to
 * drawing and are not offered while you are arranging the plan.
 */
export function Inspector({
  onApplyJoint,
  jointNote,
  run,
  onSelectWall,
  onDeleteWall,
  floating = false,
  onClose,
}: InspectorProps): JSX.Element {
  const project = useEditor((s) => s.project);
  const selectedIds = useEditor((s) => s.selected);
  const clipboard = useEditor((s) => s.clipboard);
  const busy = useEditor((s) => s.busy);
  const activeMaterialId = useEditor((s) => s.activeMaterialId);
  const setActiveMaterial = useEditor((s) => s.setActiveMaterial);
  const tool = useEditor((s) => s.tool);
  const previousTool = useEditor((s) => s.previousTool);
  useEditor((s) => s.docRevision);
  const drawing = stageOfTool(tool, previousTool) === 'draw';
  const volatileState = useVolatile();
  const model = project.activeWall.model;
  const [gap, setGap] = useState(() => formatMm(model.perpendGapMm));
  const gapField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Only when the joint itself changes, and never over a value being typed:
    // this used to resync on every document mutation and clobber half-typed
    // input (F8).
    if (document.activeElement === gapField.current) return;
    setGap(formatMm(model.perpendGapMm));
  }, [model.perpendGapMm]);

  const selected = [...selectedIds]
    .map((id) => model.get(id))
    .filter((brick): brick is NonNullable<typeof brick> => brick !== undefined);
  const bounds = selected.length > 1 ? model.boundsForIds(selectedIds) : null;

  return (
    <aside className={`panel right${floating ? ' floating' : ''}`} aria-label="Inspector">
      {floating && <div className="sheetgrip" aria-hidden />}
      {floating && onClose !== undefined && (
        <button className="panelclose" onClick={onClose} aria-label="Hide inspector">
          <X size={15} />
        </button>
      )}
      <WallsPanel run={run} onSelectWall={onSelectWall} onDeleteWall={onDeleteWall} />
      {!drawing ? null : (
        <>
      <section>
        <h2>Selection</h2>
        {selected.length === 0 && (
          <p className="readout">
            Nothing selected{'\n'}
            {plural(model.size)} in wall
          </p>
        )}
        {selected.length === 1 && (
          <p className="readout">
            {BRICK_SPECS[selected[0]!.kind].name} · {selected[0]!.rotation}°{'\n'}
            {formatMm(selected[0]!.width)} × {formatMm(selected[0]!.height)} mm{'\n'}
            X {formatMm(selected[0]!.x)}   Y {formatMm(selected[0]!.y)} mm
          </p>
        )}
        {selected.length > 1 && bounds !== null && (
          <p className="readout">
            {selected.length} bricks selected{'\n'}
            Pattern {formatMm(bounds.maxX - bounds.minX)} × {formatMm(bounds.maxY - bounds.minY)} mm{'\n'}
            From X {formatMm(bounds.minX)}, Y {formatMm(bounds.minY)} mm
          </p>
        )}
        <div className="actions">
          <button className="btn" disabled={selected.length === 0} onClick={() => run('edit.copy')}>
            Copy
          </button>
          <button className="btn" disabled={clipboard === null} onClick={() => run('edit.paste')}>
            Paste
          </button>
        </div>
        <div className="actions">
          <button
            className="btn danger"
            disabled={selected.length === 0}
            onClick={() => run('edit.delete')}
          >
            {selected.length > 1 ? `Delete ${selected.length} selected` : 'Delete selected'}
          </button>
        </div>
      </section>

      <section>
        <h2>Wall joints</h2>
        <label className="field" htmlFor="perpend">
          <input
            id="perpend"
            ref={gapField}
            data-text-input
            type="number"
            min={COORDINATE_QUANTUM_MM}
            max={MAX_PERPEND_GAP_MM}
            step={COORDINATE_QUANTUM_MM}
            value={gap}
            onChange={(event) => setGap(event.target.value)}
            onBlur={() => setGap(formatMm(model.perpendGapMm))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onApplyJoint(gap);
            }}
          />
          <span className="unit">mm</span>
        </label>
        <div className="actions">
          <button className="btn primary" disabled={busy !== null} onClick={() => onApplyJoint(gap)}>
            {busy !== null ? 'Re-coordinating…' : 'Apply perpend joint'}
          </button>
        </div>
        {jointNote !== null && <p className={`note ${jointNote.tone === 'note' ? '' : jointNote.tone}`}>{jointNote.text}</p>}
        <p className="note">
          Bed joint (between courses): fixed at {formatMm(BED_JOINT_GAP_MM)} mm. The whole wall
          re-coordinates as one undoable change.
        </p>
      </section>

      <section>
        <h2>Measurement</h2>
        <p className="readout">{volatileState.measurement || 'No measurement · Press M to begin'}</p>
      </section>

      <section>
        <h2>Materials</h2>
        <div className="swatches">
          {project.materials.map((material) => (
            <button
              key={material.id}
              className="swatch"
              aria-pressed={activeMaterialId === material.id}
              onClick={() => setActiveMaterial(material.id)}
              title={material.colour}
            >
              <i style={{ background: material.colour }} />
              {material.name}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2>This wall</h2>
        <p className="readout">
          {plural(model.size)}{'\n'}
          Perpend {formatMm(model.perpendGapMm)} mm · bed {formatMm(BED_JOINT_GAP_MM)} mm{'\n'}
          {volatileState.visibleBricks} drawn
        </p>
        <div className="actions">
          <button className="btn" onClick={() => run('view.outline')}>
            Wall outline…
          </button>
          <button className="btn danger" onClick={() => run('edit.clear')}>
            Clear wall…
          </button>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => run('file.exportPdf')}>
            Export drawing…
          </button>
        </div>
      </section>
        </>
      )}
    </aside>
  );
}
